<template>
  <div
    class="aha-presentation-editor-wrapper"
    :class="{
      'show-grid-slide': showingSlideGrid,
      'mobile-active': $store.state.activeModelDetailSlide,
    }"
  >
    <SocketError />
    <LoadingScreen v-if="!isRendering" />
    <SignUpCTAHeader v-if="isGuestUser" />

    <div
      class="z-10 h-14 md:h-15 aha-presentation-editor-header"
      :class="{'in-demo-mode': isGuestUser}"
      v-if="$store.state.presentation.id && isRendering"
    >
      <!--z-10 for header box shadow to appear -->
      <EditorHeader @update-online-count="updatePresentationSessionOnlineCount" />
    </div>
    <!-- 150 is higher than canvas's ahaslides logo (127) -->
    <public-announcement
      :z-index="150"
    />
    <div class="flex flex-1 min-h-0">
      <SideLeft v-if="!$store.state.sharePresentation" />
      <div
        class="relative flex min-h-0"
        :class="{'flex-1': !$store.state.indicators.showingSlideGrid}"
      >
        <!-- slide search popup cover -->
        <div
          v-if="isShowingSlideSearchPopup"
          class="slide-search-container"
        >
          <SlideSearchPopup
            :closeSlideSearchPopup="closeSlideSearchPopup"
          />
        </div>

        <Middle
          @nextSlideHandler="nextSlideHandler"
          class="aha-middle"
          :class="{'editor-mode': !isBackstage, 'showing-popup-search': isShowingSlideSearchPopup}"
        />
        <SideRight v-if="showSideRight" class="aha-side-right" />
      </div>
    </div>

    <BeTakenOverModal
      v-if="!isABTestCollabTeamEdit && isShowBeTakenOverModal"
      @hide="closeBeTakenOverModal()"
      :presenter="tookOverPresenter"
    />
    <BeTakenOverModalNew
      v-if="isABTestCollabTeamEdit && isShowBeTakenOverModal"
      @hide="closeBeTakenOverModal()"
      :presenter="tookOverPresenter"
    />
    <UserInactiveReloadModal v-if="showPopupUserInactive"></UserInactiveReloadModal>
    <PresenterWindowNotifyModal
      v-if="showPresenterWindowNotifyModal"
      @hide="showPresenterWindowNotifyModal = false"
    />
    <MSTeamsReloadModal
      v-if="isMSTeamsApp && isShowMSTeamsReloadModal"
      :isReloading="isReloadingMSTeams"
      @ok="() => { msTeams.reloadPresentingScreen(this) }"
    />
    <MSTeamsSharingGuideModal
      v-if="showMSTeamsSharingGuidePopup"
      @hide="showMSTeamsSharingGuidePopup = false"
    />
  </div>
</template>

<script>
import _ from 'lodash'

import * as Sentry from '@sentry/vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import moment from 'moment'
import PresentationApi from '@/api/presentation'
import ImportPPTMixin from '@/mixins/presentation/ImportPPTMixin'

// components
import LoadingScreen from '@/components-v2/common/loading/LoadingScreen.vue'
import Middle from '@/components/presentations/Middle'
import BeTakenOverModal from '@/components-v2/presentation-editor/collaboration/BeTakenOverModal.vue'
import BeTakenOverModalNew from '@/components-v2/presentation-editor/collaboration/BeTakenOverModalNew.vue'
import SlideSearchPopup from '@/components-v2/presentation-editor/new-slide/SlideSearchPopup.vue'
import SocketError from '@/components/UI/SocketError'
import socketHelper from '@/helpers/socket/socket-helper'
import PublicAnnouncement from '@/components-v2/common/global-notification/PublicAnnouncement.vue'

// handler
import PresentationEditorSocketHandler from '@/utils/presentationEditorSocketHandler.js'

// configs
import Config from '@/config'

// utils
import Utils from '@/util'
import PresentationUtils from '@/utils/presentation'
import PresentationDetailUtils from '@/utils/PresentationDetailUtils'
import GetDataPresentationDetailUtils from '@/utils/GetDataPresentationDetailUtils'
import RenderCanvasBlocksUtils from '@/utils/renderCanvasBlocksUtils'
// import CollabHelper from '@/helpers/collab/collab-helper'
import MSTeamsSharingGuideModal from '@/components-v2/ms-teams/MSTeamsSharingGuideModal.vue'

// socket
import Socket from '@/socket'

// pricing
import Pricing from '@/pricing'

// event bus
import EventBus from '@/eventBus'

// services
import cookie from '@/services/cookie'
import { handleLoadAllImageCache } from '@/utils/indexedDB/image.cache.utils'

// constants
import constants from '@/constant'
import SignUpCTAHeader from './demo-mode/SignUpCTAHeader.vue'
import EditorHeader from './editor-header/EditorHeader.vue'
import SideRight from './side-right/SideRight.vue'
import SideLeft from './side-left/SideLeft.vue'
import MSTeamsReloadModal from '@/components-v2/ms-teams/MSTeamsReloadModal.vue'
import PresenterWindowNotifyModal from './collaboration/PresenterWindowNotifyModal.vue'
import gaCategories from '@/constant/gaCategories'
import CollabHelper from '@/helpers/collab/collab-helper'
import UserInactiveReloadModal from '@/components-v2/presentation-editor/collaboration/UserInactiveReloadModal'
import workspace from '@/constant/workspace'
import msTeams from '@/utils/ms-teams'

export default {
  components: {
    UserInactiveReloadModal,
    SideLeft,
    EditorHeader,
    Middle,
    SideRight,
    LoadingScreen,
    SignUpCTAHeader,
    BeTakenOverModal,
    BeTakenOverModalNew,
    SocketError,
    PublicAnnouncement,
    PresenterWindowNotifyModal,
    SlideSearchPopup,
    MSTeamsReloadModal,
    MSTeamsSharingGuideModal,
  },
  mixins: [ImportPPTMixin],
  data() {
    return {
      joinedAt: new Date(),
      timeShowPopupInactive: Config.env === 'local' ? 1 : 3,
      refer: null,
      activeTab: 1,
      isRendering: false,
      quizwordcloudDebound: null,
      isDebouncing: false,
      isDebouncingReaction: false,
      isDebouncingOnlineCount: false,
      isDebouncingGaOnlineCount: false,
      onlineCount: 0,
      showModalUploadImg: false,
      sockets: {},
      isShowBeTakenOverModal: false,
      tookOverPresenter: null,
      showPresenterWindowNotifyModal: false,
      isShowingSlideSearchPopup: false,
      arrowPos: 0,
      isShowMSTeamsReloadModal: false,
      isReloadingMSTeams: false,
      showMSTeamsSharingGuidePopup: false,
    }
  },
  computed: {
    ...mapState({ // Do not delete anything here, since they might be called via imported functions
      presentation: (state) => state.presentation,
      presentations: (state) => state.presentations,
      sharePresentation: (state) => state.sharePresentation,
      isNewPresentation: (state) => state.isNewPresentation,
      showingSlideGrid: (state) => state.indicators.showingSlideGrid,
      slideActive: (state) => state.slideActive,
      socketId: (state) => state.socketId,
      isGuestUser: (state) => state.currentUser.isGuest,
      currentUser: (state) => state.currentUser,
      userOnline: (state) => state.userOnline,
      onlineStatus: (state) => state.onlineStatus,
      plan: (state) => state.plan,
      isPresenterWhiteLabel: (state) => state.isPresenterWhiteLabel,
      workingMode: (state) => state.workingMode,
      isAdmin: (state) => state.isAdmin,
      isPlayDemoMode: (state) => state.isPlayDemoMode,
      isDisableEditPresentation: (state) => state.isDisableEditPresentation,
      userInactiveReload: (state) => state.userInactiveReload,
      isMSTeamsApp: (state) => state.isMSTeamsApp,
    }),
    ...mapGetters(['isPresenting', 'isBackstage']),
    isQuizLobbyScreen() {
      return this.slideActive.type === 'pickAnswer'
        && this.slideActive.quizStatus === constants.QuizStatuses.LOBBY_SCREEN
    },
    isQuizResultScreen() {
      return this.slideActive.type === 'pickAnswer'
        && this.slideActive.quizStatus === constants.QuizStatuses.RESULT_SCREEN
    },
    isQuizNotLobbyScreen() {
      return this.slideActive.type == 'pickAnswer'
            && this.slideActive.quizStatus != constants.QuizStatuses.LOBBY_SCREEN
    },
    showSideRight() {
      return !this.$store.state.sharePresentation && this.currentUser.id && !this.isBackstage
    },
    isBlankPresentation() {
      const { isNewPresentation } = this.$store.state
      const { slides } = this.$store.state.presentation
      return isNewPresentation && slides.every((s) => !s.type)
    },
    isDesktopPresenting() {
      return window.innerWidth > 800 && !this.sharePresentation && !this.isBackstage
    },
    isTypeAnswerSlideType() {
      return this.slideActive.type === 'pickAnswer' && this.slideActive.slideType === 'typeAnswer'
    },
    isABTestCollabTeamEdit() {
      if (this.presentation.accountId &&
        !this.presentation.hasSharedUser && this.presentation.accountId % 2 === 0) {
        return true
      }
      return false
    },
    showPopupUserInactive() {
      return this.isABTestCollabTeamEdit &&
        !this.isPresenting &&
        this.currentUser.accountRole !== workspace.VIEWER &&
        this.userInactiveReload
      && moment().add(-this.timeShowPopupInactive, 'minutes').diff(moment(this.joinedAt)) >= 0
    },
  },
  watch: {
    slideActive() {
      GetDataPresentationDetailUtils.checkShowQuizCorrectAnswerIconReminder(this)
    },
  },
  created() {
    this.msTeams = msTeams
    // prevent editor from scrolling
    document.body.classList.add('overflow-hidden')
    document.body.classList.add('fixed') // https://ahaslides.atlassian.net/browse/AHA-2696 -> fix simiar to using legacy's .no-scroll
    this.preloadListImagePreview()
    this.syncWorkingMode()
  },
  destroyed() {
    document.body.classList.remove('overflow-hidden')
    document.body.classList.remove('fixed') // https://ahaslides.atlassian.net/browse/AHA-2696 -> fix simiar to using legacy's .no-scroll
    document.body.classList.remove('presentation-detail-layout')

    PresentationEditorSocketHandler.disconnectSocket(this)
    EventBus.$off('onKeyboard')
    EventBus.$off('updateNextSlide')
    EventBus.$off('getDataPresentationDetail')
    EventBus.$off('slide-search-popup:open')
    window.removeEventListener('message', this.handleMessageTemplatePreview)
    document.removeEventListener('keydown', this.keyDown)
    this.$store.state.audiences = {}
    this.$store.state.userOnline = 0
    this.$store.state.questions = []
    this.$store.state.tabQnaQuestion = 1
    this.$store.state.lastQuestion = {}
    this.$store.state.questionCount = 0
    this.$store.state.showLiveAudienceDemo = false
    this.$store.state.showQnaSession = false
    this.$store.state.presentation.id = null
    this.$store.state.presentationEditors = []
    this.$store.state.isDisableEditPresentation = false
    this.$store.state.openImportModal = false
    this.clearPresentationReportAction()
    this.$store.commit('unsetPresentation')
    this.clearPublicSlideTemplateAction()
  },
  async mounted() {
    window.addEventListener('message', this.handleMessageTemplatePreview, false)
    this.registerEventHistory()
    const { capture: isScreenCapture } = this.$route.query
    const { thumbnail: isCaptureContentSlideThumbnail } = this.$route.query

    if (this.$route.name === 'PresentationShare') {
      this.$store.state.sharePresentation = true
      if (isScreenCapture) {
        this.$store.state.isScreenCapture = true
      }
      if (isCaptureContentSlideThumbnail) {
        this.$store.state.isCaptureContentSlideThumbnail = true
      }
    } else {
      this.$store.state.sharePresentation = false
    }
    this.$store.state.isNewPresentation = Utils.getParamValue('new', window.location.href) === 'true'

    if (this.$store.state.isNewPresentation) {
      window.history.replaceState('', '', `/presentation/${this.$route.params.id}`)

      // after created new presentation then set flag = false
      this.$store.commit('setIsCreatingNewPresentation', false)
    }

    document.body.classList.add('presentation-detail-layout')
    if (!this.$store.state.currentUser.id && cookie.getValue('ahaToken')) {
      await this.getCurrentUserAction()
      this.handleImporting(this.$store.state.currentUser.id)
    } else if (this.$store.state.currentUser.id) {
      this.$store.state.plan = Pricing.getPlanByType(
        this.$store.state.currentUser.plan,
      )

      this.handleImporting(this.$store.state.currentUser.id)
    }

    document.onkeydown = (e) => {
      if (
        this.$store.state.slideActive.isFocusingInput &&
        Config.DISABLE_ON_FOCUS_SLIDE_KEYS.includes(e.code)
      ) {
        return
      }
      EventBus.$emit('onKeyboard', e)
    }

    this.$store.state.isTouchScreen = Utils.checkTouchScreen()

    try {
      // FIXME: Consider removing this as there should be any reason
      // why we need a specific class for safari.
      // This was added by Loi to fix an issue with safari but we lost track of the original issue.
      // @huongnguyen2152 15.05.2020
      // eslint-disable-next-line
      let isSafari = /constructor/i.test(window.HTMLElement) || (function (p) { return p.toString() === "[object SafariRemoteNotification]"; })(!window['safari'] || (typeof safari !== 'undefined' && safari.pushNotification));
      const ua = window.navigator.userAgent
      const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i)
      const webkit = !!ua.match(/WebKit/i)
      const iOSSafari = iOS && webkit && !ua.match(/CriOS/i)
      if (isSafari || iOSSafari) {
        document.body.classList.add('safari-browser')
      }
    } catch (error) {
      Sentry.captureException(error)
    }

    PresentationEditorSocketHandler.handleSocketEvent(this)

    EventBus.$on('updateNextSlide', _.debounce(this.handleUpdateNextSlide, 400))
    EventBus.$on('getDataPresentationDetail', () => {
      this.getDataPresentationDetail()
    })
    EventBus.$on('slide-search-popup:open', this.openSlideSearchPopup)

    // Now load
    await this.getDataPresentationDetail()
    this.initSocket(this.presentation.uniqueAccessCode)
    this.joinCollaborativeEditingRoom(this.presentation)

    if (this.isBackstage) {
      if (!JSON.parse(localStorage.getItem('PresenterWindowNotifyModal'))) {
        setTimeout(() => {
          this.showPresenterWindowNotifyModal = true
        }, 1000)
      }
    }
    if (Utils.isPageReloaded()) {
      socketHelper.backstageControl({
        uniqueAccessCode: this.presentation.uniqueAccessCode,
        type: 'reload',
      })
    }

    if (this._isPresentationEditorPage(this.$route)
      && this.currentUser && this.currentUser.id && !this.presentation.takenOverBy) {
      PresentationApi.takeOver(this.presentation.id)
    }

    // Support realtime in "share" view
    if (this.$route.name === 'PresentationShare') {
      // Using 'join-user' event for convenient :(
      Socket.getSocket().emit('join-user', `${this.$store.state.presentation.uniqueAccessCode}-presenter`)
    }
    // dipslay slide search by default if just created a blank prez
    if (this.isBlankPresentation) {
      this.isShowingSlideSearchPopup = true
      this.patchPresentationAction({
        isShowingSlideSearch: true,
      })
    }

    this.handleTrackingReferPublicTemplate()
    this.getLastSlideActiveForCollab()
    msTeams.initialize(this)
  },
  methods: {
    ...mapActions([
      'getCurrentUserAction',
      'patchPresentationAction',
      'setErrorOnInternalServerAction',
      'updateSlideActiveAction',
      'patchSlideByIdAction',
      'checkSlideMovingDirectionAction',
      'undoAction',
      'redoAction',
      'clearHistory',
    ]),
    ...mapActions('presentationReport', ['clearPresentationReportAction']),
    ...mapActions('presentationChat', ['audienceChangeStatusAction']),
    ...mapActions('contentSlideTemplates', ['clearPublicSlideTemplateAction']),
    getLastSlideActiveForCollab() {
      if (this.presentation.hasSharedUser && this.isDisableEditPresentation) {
        return
      }
      if (this.presentation.accountId && !this.presentation.hasSharedUser &&
        this.presentation.accountId % 2 === 1 && this.isDisableEditPresentation) {
        return
      }
      CollabHelper.getListUsersEditPresentation(true)
    },
    registerEventHistory() {
      this.clearHistory()
      document.addEventListener('keydown', this.keyDown)
    },
    updatePresentationSessionOnlineCount: _.throttle((presentationId, onlineCount) => {
      PresentationApi.updateOnlineCountByPresentationId(presentationId, { onlineCount })
    }, 5000),
    handleTrackingReferPublicTemplate() {
      this.refer = Utils.getParamValue('refer', window.location.href)
      if (this.refer === constants.referPublicTemplate) {
        const query = { ...this.$route.query }
        delete query.refer
        this.$router.replace({ query })
        this.$store.state.isReferPublicTemplate = true
        Utils.gaTrackEvent(gaCategories.CATEGORY_PUBLIC_TEMPLATE_DEMO_MODE, 'Create new presentation', 'Use public template')
      }
    },
    keyDown(e) {
      const evtObj = window.event ? window.event : e
      if ((evtObj.ctrlKey || e.metaKey) && ((evtObj.keyCode === 90 && evtObj.shiftKey) || (evtObj.keyCode === 89))) {
        e.preventDefault()
        this.redoAction()
        return
      }
      if (evtObj.keyCode === 90 && (evtObj.ctrlKey || e.metaKey)) {
        e.preventDefault()
        this.undoAction()
      }
    },
    closeBeTakenOverModal() {
      EventBus.$emit('closePresentationPreview')
      this.isShowBeTakenOverModal = false
    },
    handleMessageTemplatePreview(msg) {
      if (msg.data.type === constants.Events.EVENT_CHANGE_SLIDE) {
        const { presenterDomain } = Config
        let whiteLabelDomain = null
        if ((this.presentation && this.presentation.whiteLabelLink)
        || this.$store.state.presentation.whiteLabelLink) {
          if (this.presentation && this.presentation.whiteLabelLink) {
            whiteLabelDomain = this.presentation.whiteLabelLink
          } else {
            whiteLabelDomain = this.$store.state.presentation.whiteLabelLink
          }
        }
        if (whiteLabelDomain && `https://${whiteLabelDomain}` !== msg.origin) {
          return
        }
        if (presenterDomain !== msg.origin && Config.ssrDomain !== msg.origin) {
          return
        }
        const { id } = msg.data.slide
        EventBus.$emit('template-preview:go-to-slide', id)
      }
    },

    preloadListImagePreview() {
      const slideTypePreview = {}
      constants.allTypeSections.forEach((item) => {
        item.slideTypes.forEach((objType) => {
          if (objType.preview) {
            slideTypePreview[objType.preview] = {
              type: objType.preview,
              url: objType.previewUrl,
            }
          }
        })
      })

      const promiseAll = []
      Object.keys(slideTypePreview).forEach((key) => {
        promiseAll.push(Utils.preloadImage(slideTypePreview[key]))
      })
      Promise.all(promiseAll).then((results) => {
        results.forEach((item) => {
          slideTypePreview[item.type] = item
        })
        this.$store.state.slideTypePreview = slideTypePreview
      })
    },

    _isPresentationEditorPage(route) {
      return route.name === 'PresentationDetail' || route.name === 'PresentationBackstage'
    },

    joinCollaborativeEditingRoom(presentation) {
      // AHA-5534: Only join presenter collaborative room if active route
      // is the actual Presentation Editor (not the Share page, or the Presenting mode one)
      if (!presentation) return
      if (!presentation.id) return
      if (!presentation.uniqueAccessCode) {
        Sentry.captureMessage(`uniqueAccessCode not found. PresentationId: ${presentation.id}`, 'debug')
        return
      }
      if (
        this._isPresentationEditorPage(this.$route)
      ) {
        socketHelper.joinRoomPresentation(presentation.uniqueAccessCode)
        socketHelper.joinRoomSlide(presentation.uniqueAccessCode, presentation.activeSlide)
      }
    },

    async getDataPresentationDetail(options = { cachedContentSlides: true }) {
      let { id: presentationId } = this.$route.params
      const { beTakenOver } = this.$route.params
      const { embedded } = this.$route.query
      if (beTakenOver || embedded === 'true') {
        options.cachedContentSlides = false
      }

      const url = GetDataPresentationDetailUtils.getPresentationDetailURL(this.sharePresentation)
      if (this.sharePresentation) {
        if (Utils.getShareCodeFromSlug(presentationId) !== null) {
          presentationId = Utils.getShareCodeFromSlug(presentationId)
        }
      }
      const payloadDataPresentation = {
        socketId: Socket.getSocketId(),
        admin: this.isAdmin,
      }

      try {
        const res = await this.$http.get(url + presentationId, {
          params: payloadDataPresentation,
        })
        if (res.body.TakenOverByUser && this.currentUser &&
            this.currentUser.email !== res.body.TakenOverByUser.email) {
          options.cachedContentSlides = false
        }

        await this.handleDataPresentationDetail(res, {
          cachedContentSlides: options.cachedContentSlides,
        })

        const { presenting, preview: isPreviewPresentation } = this.$route.query

        if (isPreviewPresentation) {
          this.$store.state.isPreview = true
        }

        // If reload, presentation's presenting -> set fullscreen mode, else set edit mode
        const isPresentationOwnerAndPresenting = !presenting && presentationId &&
          !isPreviewPresentation && !this.isAdmin && this.presentation.presenting &&
            this.currentUser.id == this.presentation.takenOverBy
        const isRememberedFullscreenNotDemoMode = this.currentUser.rememberSettings &&
        this.currentUser.rememberSettings.fullscreen && !this.isPlayDemoMode
        GetDataPresentationDetailUtils.checkToSetFullScreenMode(isPresentationOwnerAndPresenting,
          isRememberedFullscreenNotDemoMode, this)

        // When play quiz, reload -> back to lobby screen
        if (!this.isAdmin && this.isQuizNotLobbyScreen) {
          await GetDataPresentationDetailUtils.updateSlideToQuizLobbyScreen(this)
        }
        // handle when presenting need update on db
        if ((presenting && !this.presentation.presenting &&
            !this.isAdmin && !this.sharePresentation && !this.isBackstage) || this.$store.state.isMSTeamsApp) {
          this._updatePresentingMode()
        }
        GetDataPresentationDetailUtils.checkShowQuizCorrectAnswerIconReminder(this)
        EventBus.$emit('updateLastSlide')
      } catch (err) {
        GetDataPresentationDetailUtils
          .handleGetDataPresentationDetailFailed(err, this, presentationId)
      }
    },

    _handleShowResultsOnQuizSlide() {
      if (this.presentation.manualRevealCorrectAnswers && !this.slideActive.showCorrectOption) {
        return
      }
      if (this.isTypeAnswerSlideType) {
        EventBus.$emit('eventShowQuizTypeAnswer')
      }
      setTimeout(() => {
        this.updateSlideActiveAction({
          showCorrectOption: true,
          hideResult: false,
        })
      }, 2500)
    },

    _updatePresentingMode() {
      if (!this.$store.state.slideActive.id || !this.$store.state.presentation.id) return
      const payload = {
        presenting: true,
        socketId: Socket.getSocketId(),
        activeSlidePreseting: this.$store.state.slideActive.id,
        activeSlideAccessCode: this.$store.state.presentation.uniqueAccessCode,
      }
      if (this.$store.state.isMSTeamsApp) {
        payload.msTeams = true
      }
      this.$http
        .put(Config.api.updatePresentation + this.$store.state.presentation.id, payload)
        .then(() => {
        }, () => {})
      this.$store.state.presentation.presenting = true
    },

    _checkAndUpdateHasResults() {
      if (!this.presentation.hasResults) {
        this._updatePresentationHasResults(true)
      }
    },

    async handleDataPresentationDetail(res, { cachedContentSlides = true }) {
      const _dispatch = this.$store.dispatch
      const noAudienceAdmissionFields = (res.body.audienceAdmission &&
        res.body.audienceAdmission.isAudienceAdmission &&
        !res.body.audienceAdmission.fields) || !res.body.audienceAdmission

      this.sockets[Socket.getSocketId()] =
        PresentationDetailUtils.setPresentationSockets(this.currentUser)

      res.body.slides = res.body.Slides

      this.patchPresentationAction({
        submitSlideLookup: res.body.submitSlideLookup || null,
      })

      await handleLoadAllImageCache(res.body.slides)

      this.$store.state.userOnline = res.body.userOnline

      this.$store.state.onlineStatus = PresentationDetailUtils.setOnlineStatus(this.currentUser,
        this.sharePresentation, this.userOnline, this.onlineStatus, this.plan)

      PresentationDetailUtils.setPresentationLanguage(res, _dispatch)

      PresentationDetailUtils.setBrowserTitle(res, this.isPresenterWhiteLabel, this.isBackstage)

      PresentationDetailUtils.sortAndUpdateSlides(res, this)

      this.$store.dispatch('setPresentation', res.body)

      if (noAudienceAdmissionFields) {
        this._setDefaultFields(res.body)
        this.updatePresentation({
          audienceAdmission: this.presentation.audienceAdmission,
        })
      }

      PresentationDetailUtils.handleActiveTab(this.isNewPresentation,
        this.sharePresentation, _dispatch, res)
      EventBus.$emit('setPresentationDataDone')

      if (!res.body.activeSlide && res.body.slides.length) {
        if (!res.body.slides[0].type) {
          this.$store.state.isNewPresentation = true
        }
      }

      PresentationDetailUtils.setPrezFontFamily(this.presentation.fontFamily)

      PresentationDetailUtils.getAudienceOnline(res, this.slideActive, this.presentation)

      this.handleCacheImage(res.body)
      this.getQuestions(res.body.id, res.body.slides)
      if (this.isABTestCollabTeamEdit) {
        cachedContentSlides = false
      }
      await RenderCanvasBlocksUtils.loadContentSlidesCanvasBlocks(this, res,
        { cachedContentSlides })
      setTimeout(async () => {
        this.isRendering = true
        await CollabHelper.getListUsersEditPresentation(false, true)
        EventBus.$emit('eventHandleSetValueTransition')
      }, 500)
      PresentationDetailUtils.detectScreenAndSetTransition()
    },

    _setDefaultFields(detailPresentation) {
      const arr = []
      if (!detailPresentation.audienceAdmission) {
        detailPresentation.audienceAdmission = {}
      }
      if (detailPresentation.audienceAdmission && !detailPresentation.audienceAdmission.heading) {
        this.$store.state.presentation.audienceAdmission.heading = this.$t('welcomeToMyPresentation')
      }
      Object.values(constants.audienceAdmissionFields).forEach((item) => {
        this._setLabelToLanguage(item)
        arr.push(item)
      })
      this.$store.state.presentation.audienceAdmission.fields = arr
    },

    _setLabelToLanguage(option) {
      if (option.type === 'name') {
        option.label = this.$t('enterYourName')
      }
      if (option.type === 'team') {
        option.label = this.$t('enterYourOrganisation')
      }
      if (option.type === 'email') {
        option.label = this.$t('enterYourEmail')
      }
    },
    syncWorkingMode() {
      const currentMode = window.localStorage.getItem(constants.workspace.WORKING_MODE)
      if (currentMode && this.$store.state.workingMode !== currentMode) {
        this.$store.state.workingMode = currentMode
      }
    },

    async _updatePresentationHasResults(hasResults) {
      const payload = {
        hasResults,
      }
      PresentationUtils.updatePresentationHasResults(this.presentation, hasResults)
      setTimeout(() => {
        this.patchPresentationAction(payload)
      }, 500)
    },

    initSocket(channel) {
      PresentationEditorSocketHandler.initSocket(channel, this)
    },

    _setTeamData: _.debounce(function () {
      if (this.$store.state.sharePresentation) return
      const { teamSize } = this.$store.state.presentation
      const teamCount = this._getTeamCount()
      const teamData = {}
      Object.keys(teamCount)
        .forEach((id) => {
          teamData[id] = {
            teamCount: teamCount[id],
            full: teamCount[id] >= teamSize,
          }
        })
      this.$http.post(Config.api.checkQuizTeamMember, {
        teamData,
        accessCode: this.$store.state.presentation.uniqueAccessCode,
      })
        .then(() => {}, () => {})
    }, 500),

    _getTeamCount() {
      const teams = {}

      Object
        .keys(this.$store.state.audiences)
        .forEach((key) => {
          const teamId = this.$store.state.audiences[key].audienceTeam

          teams[teamId] = teams[teamId] || 0
          teams[teamId] += 1
        })

      return teams
    },

    _getAndSetPayloadAudienceTeamName(audience) {
      this._setTeamData()
      const { teamData } = this.$store.state.presentation
      const audienceTeam = teamData.find((team) => team.id === audience.audienceTeam)
      if (audienceTeam) audience.audienceTeamName = audienceTeam.name
    },

    _handleEventAudienceOnOff: _.throttle(function () {
      if (this.slideActive.type === 'spinnerWheel') {
        EventBus.$emit('updateEntries')
      }
    }, 1000),

    _getAudienceOnlineJoinQuiz: _.throttle(function () {
      let count = 0
      Object.keys(this.$store.state.audiences).forEach((key) => {
        if (!this.$store.state.audiences[key].offline) {
          count++
        }
      })
      this.$store.state.countPlayer = count
      this._getGameLobbyQuiz()
    }, 250),

    _getGameLobbyQuiz: _.throttle(function (doc) {
      if (!this.$store.state.presentation.teamPlay) {
        EventBus.$emit('quiz-bus', doc)
      } else {
        EventBus.$emit('gamelobby-quiz-team', doc)
      }
    }, 1000),

    updatePresentation(payload) {
      if (this.$store.state.sharePresentation) return
      this.$http
        .put(Config.api.updatePresentation + this.$route.params.id, payload)
    },

    nextSlideHandler(currentIndex) {
      const nextSlide = this.$store.state.presentation.slides[currentIndex + 1]
      if (nextSlide && this.$store.state.sharePresentation) {
        const arr = []
        this.buildImagesList(nextSlide, arr)
        this.preloadImages(arr)
      }
    },

    handleCacheImage(doc) {
      const arr = []
      if (doc && doc.slides) {
        if (doc.logo) {
          arr.push(doc.logo.thumbnail)
          arr.push(doc.logo.large)
        }

        // If not in sharing, preload all images, else preload only 2 slides
        const preload = !this.$store.state.sharePresentation ? doc.slides.length : 2
        doc.slides.slice(0, preload).forEach((slide) => this.buildImagesList(slide, arr))
      }

      this.preloadImages(arr)
    },

    buildImagesList(slide, arr) {
      if (slide.image) {
        arr.push(slide.image.thumbnail)
        arr.push(slide.image.xLarge)
      }

      if (slide.backgroundImage) {
        arr.push(slide.backgroundImage.thumbnail)
        arr.push(slide.backgroundImage.xLarge)
      }

      slide.SlideOptions.forEach((option) => {
        if (option.image) {
          arr.push(option.imageCropped)
        }
      })
    },

    preloadImages(array) {
      if (!this.preloadImages.list) {
        this.preloadImages.list = []
      }
      const { list } = this.preloadImages
      for (let i = 0; i < array.length; i++) {
        const img = new Image()
        img.onload = (() => {
          const index = list.indexOf(this)
          if (index !== -1) {
            // remove image from the array once it's loaded
            // for memory consumption reasons
            list.splice(index, 1)
          }
        })
        list.push(img)
        img.src = array[i]
      }
    },

    getQuestions(id, allSlides) {
      this.$http
        .get(Config.api.getQuestion, {
          params: {
            id,
          },
        })
        .then(
          (res) => {
            Utils.updateDataQuestionAnswer(
              res.body,
              this.$store.state.tabQnaQuestion,
            )
            const hasResults = PresentationUtils.checkPresentationHasResults(allSlides)
            const needUpdateHasResults = this.presentation.hasResults !== hasResults
            if (needUpdateHasResults) {
              this._updatePresentationHasResults(PresentationUtils.checkPresentationHasResults(allSlides))
            }
          },
          () => {},
        ).catch((err) => {
          this.$router.push('/apps/presentations')
          this.setErrorOnInternalServerAction('Failed to get questions list')
          throw err
        })
    },

    getQuizIndex(id) {
      if (!id) return null
      const quizs = this.$store.state.presentation.slides.filter((item) => {
        return item.type == 'pickAnswer'
      })
      const index = Utils.getIndexSlide(quizs, id)
      return {
        index: index + 1,
        totalQuiz: quizs.length,
      }
    },

    async handleUpdateNextSlide(idSlide, source) {
      const presentationId =
        this.$store.state.presentation.id || this.$route.params.id
      if (!idSlide || !presentationId || this.$store.state.sharePresentation) {
        return
      }

      if (this.$store.state.isDisableEditPresentation && source !== 'fromClearDataSlide') {
        const payloadNextSlide = {
          accessCode: this.$store.state.presentation.uniqueAccessCode,
          nextSlideId: idSlide,
        }
        this.$http.post(Config.api.collabNextSlide, payloadNextSlide)
        return
      }

      const indexSlide = this.$store.state.presentation.slides.findIndex((item) => {
        return item.id === idSlide
      })

      this.$store.dispatch('setSyncPresentation', 1)
      const payloadSlide = {
        activeSlide: idSlide,
        socketId: Socket.getSocketId(),
        accessCode: this.$store.state.presentation.uniqueAccessCode,
        timestamp: new Date().getTime(),
        source,
      }

      if (this.$store.state.slideActive.quizStatus !== -1) {
        payloadSlide.quizStatus = constants.QuizStatuses.LOBBY_SCREEN
      }

      let objQuiz = null
      if (this.$store.state.presentation.slides &&
      this.$store.state.presentation.slides[indexSlide] &&
      this.$store.state.presentation.slides[indexSlide].type === 'pickAnswer') {
        objQuiz = this.getQuizIndex(idSlide)

        if (objQuiz) {
          payloadSlide.questionCount = objQuiz.totalQuiz
          payloadSlide.questionIndex = objQuiz.index
        }

        if (this.$store.state.continueQuiz) {
          payloadSlide.quizStatus = constants.QuizStatuses.RULE_SCREEN
        }
      }
      this.$store.state.swapLeaderboard = false
      if (this.$store.state.presentation.slides[indexSlide].type === 'leaderboard') {
        this.$store.dispatch('setSyncPresentation', 2)
        return
      }
      try {
        this.$http.put(Config.api.updateNextSlide + presentationId, payloadSlide)
        this.checkSlideMovingDirectionAction(this.slideActive.id)
        EventBus.$emit('enableQuizStart', idSlide)
        this.$store.dispatch('setSyncPresentation', 2)
      } catch (e) {
        this.$store.dispatch('setSyncPresentation', 3)
        Sentry.captureException(e)
      }
    },

    openSlideSearchPopup(pos) {
      this.arrowPos = pos
      this.isShowingSlideSearchPopup = true
      this.patchPresentationAction({
        isShowingSlideSearch: true,
      })
    },
    closeSlideSearchPopup() {
      this.isShowingSlideSearchPopup = false
      this.patchPresentationAction({
        isShowingSlideSearch: false,
      })
    },

  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variable.scss";

.aha-presentation-editor-wrapper {
  @apply flex flex-col h-full absolute w-full;

  &.show-grid-slide {
    .aha-side-left {
      @apply flex-1;
    }

    .middle-presentation {
      @apply flex-none;
    }
  }
}

.aha-presentation-editor-header.in-demo-mode {
  @apply mt-10;
}

.aha-side-left {
  transition: all .3s ease;
}

@media (max-width: 768px) {
  .editor-mode.aha-middle {
    display: none;
  }

  .aha-side-left {
    max-width: 100%;
    @apply w-full;
  }
}

.slide-search-container {
  background-color: rgba(0, 0, 0, .8); // https://stackoverflow.com/a/14525563
  @apply h-full w-full absolute top-0 left-0 z-10;

  @media (max-width: $ipad-breakpoint) {
    display: none;
  }

  /* if backdrop support: very transparent and blurred */
  // https://stackoverflow.com/a/44366154
  // Fix firefox not support backdrop-filter
  @supports ((-webkit-backdrop-filter: none) or (backdrop-filter: none)) {
    background: rgba(0, 0, 0, 0.4);
    -webkit-backdrop-filter: blur(10px);
    backdrop-filter: blur(10px);
  }
}

.aha-middle {
  &.showing-popup-search {
    &::v-deep .live-demo-audience {
      display: none;
    }
  }
}
</style>
